import { faChevronDown, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef, useCallback, useMemo, useState } from "react";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";
import { Tooltip } from "../tooltip/Tooltip";
import { SidebarNavProvider, useSidebarNavContext } from "./context";

import type { FC, HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode } from "react";
import type { Key } from "react-stately";
import type { VariantProps } from "tailwind-variants";
import type { ForwardRefComponentWithSubcomponents } from "../../utils/types";
import type { SidebarNavContextValue, SidebarNavSize } from "./context";

// ============================================================================
// Variants
// ============================================================================

const sidebarNavVariants = tv({
  base: "flex w-full flex-col gap-1",
  variants: {
    theme: {
      light: "bg-white dark:bg-gray-900",
      dark: "bg-[#001529] dark:bg-[#001529]",
    },
  },
  defaultVariants: {
    theme: "light",
  },
});

const itemVariants = tv({
  base: [
    "group flex w-full cursor-pointer items-center gap-3 bg-transparent px-4 py-2.5 text-left no-underline transition-colors",
    "font-normal",
    "outline-none",
    "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue",
  ],
  variants: {
    theme: {
      light: ["text-gray-700 dark:text-gray-300", "hover:bg-gray-100 dark:hover:bg-gray-800"],
      dark: ["text-gray-300", "hover:bg-blue-900 hover:text-white", "font-semibold"],
    },
    size: {
      sm: "text-sm",
      lg: "text-base",
    },
    selected: {
      true: "",
      false: "",
    },
    disabled: {
      true: "cursor-not-allowed opacity-50 hover:bg-transparent dark:hover:bg-transparent",
      false: "",
    },
  },
  compoundVariants: [
    {
      theme: "light",
      selected: true,
      className: "bg-blue-50 text-blue dark:bg-blue-900/30 dark:text-blue-400",
    },
    {
      theme: "dark",
      selected: true,
      className: "bg-blue-500 font-semibold text-white hover:bg-blue-600",
    },
  ],
  defaultVariants: {
    theme: "light",
    size: "sm",
    selected: false,
    disabled: false,
  },
});

const itemGroupVariants = tv({
  base: "flex flex-col gap-0.5",
  variants: {
    theme: {
      light: "",
      dark: "",
    },
  },
  defaultVariants: {
    theme: "light",
  },
});

const itemGroupTitleVariants = tv({
  base: "px-4 py-2 text-xs font-semibold uppercase tracking-wider",
  variants: {
    theme: {
      light: "text-gray-500 dark:text-gray-400",
      dark: "text-gray-500",
    },
  },
  defaultVariants: {
    theme: "light",
  },
});

const dividerVariants = tv({
  base: "mx-4 my-2 h-px",
  variants: {
    theme: {
      light: "bg-gray-200 dark:bg-gray-700",
      dark: "bg-blue-900",
    },
  },
  defaultVariants: {
    theme: "light",
  },
});

const iconWrapperVariants = tv({
  base: "flex shrink-0 items-center justify-center",
  variants: {
    theme: {
      light: "text-gray-500 dark:text-gray-400",
      dark: "text-gray-400 group-hover:text-white",
    },
    selected: {
      true: "",
      false: "",
    },
    size: {
      default: "size-5",
      large: "size-6 text-lg",
    },
  },
  compoundVariants: [
    {
      theme: "light",
      selected: true,
      className: "text-blue dark:text-blue-400",
    },
    {
      theme: "dark",
      selected: true,
      className: "text-white",
    },
  ],
  defaultVariants: {
    theme: "light",
    selected: false,
    size: "default",
  },
});

const chevronVariants = tv({
  base: "ml-auto size-3 shrink-0 transition-transform duration-200",
  variants: {
    theme: {
      light: "text-gray-400 dark:text-gray-500",
      dark: "text-gray-500",
    },
    expanded: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    theme: "light",
    expanded: false,
  },
});

const collapsibleChildrenVariants = tv({
  base: "flex flex-col",
  variants: {
    theme: {
      light: "bg-gray-50 dark:bg-gray-800",
      dark: "bg-blue-900",
    },
  },
  defaultVariants: {
    theme: "light",
  },
});

const childItemVariants = tv({
  base: [
    "flex w-full cursor-pointer items-center gap-3 bg-transparent py-2.5 pl-12 pr-4 text-left no-underline transition-colors",
    "font-normal",
    "outline-none",
    "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue",
  ],
  variants: {
    theme: {
      light: ["text-gray-700 dark:text-gray-300", "hover:bg-gray-100 dark:hover:bg-gray-800"],
      dark: ["text-gray-300", "hover:bg-blue-900 hover:text-white"],
    },
    size: {
      sm: "text-sm",
      lg: "text-base",
    },
    selected: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      theme: "light",
      selected: true,
      className: "bg-blue-50 text-blue dark:bg-blue-900/30 dark:text-blue-400",
    },
    {
      theme: "dark",
      selected: true,
      className: "bg-blue-500 text-white hover:bg-blue-600",
    },
  ],
  defaultVariants: {
    theme: "light",
    size: "sm",
    selected: false,
  },
});

// ============================================================================
// Types
// ============================================================================

export interface SidebarNavItemData {
  key: Key;
  label?: ReactNode;
  icon?: ReactNode;
  href?: string;
  onClick?: (e: MouseEvent) => void;
  children?: SidebarNavItemData[];
  type?: "group" | "divider";
  disabled?: boolean;
  className?: string;
  "data-testid"?: string;
}

export interface SidebarNavProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect" | "onClick">,
    VariantProps<typeof sidebarNavVariants> {
  /** Key of the selected item */
  selectedKey?: Key;
  /** Keys of expanded collapsible items */
  expandedKeys?: Key[];
  /** Keys of initially expanded collapsible items (uncontrolled) */
  defaultExpandedKeys?: Key[];
  /** Callback when item is selected */
  onSelect?: (info: { key: Key }) => void;
  /** Callback when item is clicked (alias for onSelect for antd compatibility) */
  onClick?: (info: { key: Key }) => void;
  /** Callback when a collapsible item is expanded/collapsed */
  onExpandChange?: (expandedKeys: Key[]) => void;
  /** Items to render (alternative to children) */
  items?: SidebarNavItemData[];
  /** Whether the sidebar is collapsed (show only icons) */
  isCollapsed?: boolean;
  /** Size of nav items — "sm" (default 14px) or "lg" (16px) */
  size?: SidebarNavSize;
}

export interface SidebarNavItemProps extends Omit<HTMLAttributes<HTMLDivElement>, "onClick"> {
  /** Unique key for this item */
  itemKey?: Key;
  /** Icon element */
  icon?: ReactNode;
  /** Whether item is disabled */
  disabled?: boolean;
  onClick?: (e: MouseEvent) => void;
}

export interface SidebarNavItemGroupProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  /** Group title */
  title?: ReactNode;
}

export type SidebarNavDividerProps = HTMLAttributes<HTMLDivElement>;

// ============================================================================
// Components
// ============================================================================

/**
 * SidebarNav.Item - Individual navigation item
 */
const SidebarNavItem = forwardRef<HTMLDivElement, SidebarNavItemProps>(
  ({ children, className, itemKey, icon, disabled, onClick, ...props }, ref) => {
    const { selectedKeys, onSelect, theme, size } = useSidebarNavContext();
    const isSelected = itemKey !== undefined && selectedKeys.has(itemKey);

    const handleClick = useCallback(
      (e: MouseEvent) => {
        if (disabled) return;
        onClick?.(e);
        if (itemKey !== undefined) {
          onSelect?.(itemKey);
        }
      },
      [disabled, onClick, itemKey, onSelect]
    );

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (e.key === "Enter") {
          e.preventDefault();
          if (itemKey !== undefined) {
            onSelect?.(itemKey);
          }
        }
      },
      [disabled, itemKey, onSelect]
    );

    return (
      <div
        ref={ref}
        role="menuitem"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        data-selected={isSelected}
        className={itemVariants({
          theme,
          size,
          selected: isSelected,
          disabled: !!disabled,
          className,
        })}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {icon && (
          <span className={iconWrapperVariants({ theme, selected: isSelected })}>{icon}</span>
        )}
        <span className="flex-1 truncate">{children}</span>
      </div>
    );
  }
);
SidebarNavItem.displayName = "SidebarNav.Item";

/**
 * SidebarNav.ItemGroup - Group of items with optional title
 */
const SidebarNavItemGroup = forwardRef<HTMLDivElement, SidebarNavItemGroupProps>(
  ({ children, className, title, ...props }, ref) => {
    const { theme } = useSidebarNavContext();

    return (
      <div ref={ref} role="group" className={itemGroupVariants({ theme, className })} {...props}>
        {title && <div className={itemGroupTitleVariants({ theme })}>{title}</div>}
        {children}
      </div>
    );
  }
);
SidebarNavItemGroup.displayName = "SidebarNav.ItemGroup";

/**
 * SidebarNav.Divider - Visual separator between items
 */
const SidebarNavDivider: FC<SidebarNavDividerProps> = ({ className, ...props }) => {
  const { theme } = useSidebarNavContext();

  return <div role="separator" className={dividerVariants({ theme, className })} {...props} />;
};
SidebarNavDivider.displayName = "SidebarNav.Divider";

/**
 * Collapsible item with children - renders as expandable menu item
 */
const CollapsibleItem: FC<{
  item: SidebarNavItemData;
  theme: SidebarNavContextValue["theme"];
  size: SidebarNavContextValue["size"];
  selectedKeys: Set<Key>;
  expandedKeys: Set<Key>;
  onSelect: SidebarNavContextValue["onSelect"];
  onToggleExpand: SidebarNavContextValue["onToggleExpand"];
  isCollapsed: boolean;
}> = ({ item, theme, size, selectedKeys, expandedKeys, onSelect, onToggleExpand, isCollapsed }) => {
  const isExpanded = expandedKeys.has(item.key);
  // Check if any child is selected to highlight the parent
  const hasSelectedChild = item.children?.some(child => selectedKeys.has(child.key)) ?? false;

  const handleToggle = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onToggleExpand?.(item.key);
    },
    [item.key, onToggleExpand]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onToggleExpand?.(item.key);
      }
    },
    [item.key, onToggleExpand]
  );

  const chevronIcon = useMemo(
    () => (
      <FontAwesomeIcon
        icon={isExpanded ? faChevronDown : faChevronRight}
        className={chevronVariants({ theme, expanded: isExpanded })}
      />
    ),
    [isExpanded, theme]
  );

  // When collapsed, just show icon with tooltip
  if (isCollapsed) {
    const itemKey = String(item.key);
    const collapsedEl = (
      <div
        role="menuitem"
        tabIndex={0}
        data-testid={item["data-testid"]}
        className={itemVariants({
          theme,
          size,
          selected: hasSelectedChild,
          className: cn("justify-center px-0 py-3", item.className),
        })}
      >
        {item.icon && (
          <span
            className={iconWrapperVariants({ theme, selected: hasSelectedChild, size: "large" })}
          >
            {item.icon}
          </span>
        )}
      </div>
    );

    if (item.label) {
      return (
        <Tooltip key={itemKey} content={item.label} placement="right" color="default">
          {collapsedEl}
        </Tooltip>
      );
    }

    return <div key={itemKey}>{collapsedEl}</div>;
  }

  return (
    <div key={String(item.key)}>
      <div
        role="menuitem"
        tabIndex={0}
        aria-expanded={isExpanded}
        data-testid={item["data-testid"]}
        className={itemVariants({
          theme,
          size,
          selected: hasSelectedChild && !isExpanded,
          className: item.className,
        })}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        {item.icon && (
          <span
            className={iconWrapperVariants({ theme, selected: hasSelectedChild && !isExpanded })}
          >
            {item.icon}
          </span>
        )}
        <span className="flex-1 truncate">{item.label}</span>
        {chevronIcon}
      </div>
      {isExpanded && item.children && (
        <div className={collapsibleChildrenVariants({ theme })}>
          {item.children.map(child => {
            const isChildSelected = selectedKeys.has(child.key);
            const childClassName = childItemVariants({
              theme,
              size,
              selected: isChildSelected,
              className: child.className,
            });
            const childContent =
              typeof child.label === "string" ? (
                child.label
              ) : (
                <span className="flex-1 truncate">{child.label}</span>
              );

            const handleChildClick = (e: MouseEvent) => {
              child.onClick?.(e);
              onSelect?.(child.key);
            };

            const handleChildKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
              if (child.disabled) return;
              if (e.key === "Enter") {
                e.preventDefault();
                onSelect?.(child.key);
              }
            };

            return child.href && !child.disabled ? (
              <a
                key={String(child.key)}
                href={child.href}
                role="menuitem"
                data-selected={isChildSelected}
                data-testid={child["data-testid"]}
                className={childClassName}
                onClick={handleChildClick}
              >
                {childContent}
              </a>
            ) : (
              <div
                key={String(child.key)}
                role="menuitem"
                tabIndex={child.disabled ? -1 : 0}
                aria-disabled={child.disabled}
                data-selected={isChildSelected}
                data-testid={child["data-testid"]}
                className={childClassName}
                onClick={handleChildClick}
                onKeyDown={handleChildKeyDown}
              >
                {childContent}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Recursive item renderer for items prop
 */
const renderItem = (
  item: SidebarNavItemData,
  theme: SidebarNavContextValue["theme"],
  size: SidebarNavContextValue["size"],
  selectedKeys: Set<Key>,
  expandedKeys: Set<Key>,
  onSelect: SidebarNavContextValue["onSelect"],
  onToggleExpand: SidebarNavContextValue["onToggleExpand"],
  isCollapsed: boolean
): ReactNode => {
  if (item.type === "divider") {
    if (isCollapsed) return null;
    return <SidebarNavDivider key={String(item.key)} />;
  }

  // Explicit group type - render as group with title (hidden when collapsed)
  if (item.type === "group") {
    if (isCollapsed) {
      // When collapsed, just render children without group wrapper
      return item.children?.map(child =>
        renderItem(
          child,
          theme,
          size,
          selectedKeys,
          expandedKeys,
          onSelect,
          onToggleExpand,
          isCollapsed
        )
      );
    }
    return (
      <SidebarNavItemGroup key={String(item.key)} title={item.label}>
        {item.children?.map(child =>
          renderItem(
            child,
            theme,
            size,
            selectedKeys,
            expandedKeys,
            onSelect,
            onToggleExpand,
            isCollapsed
          )
        )}
      </SidebarNavItemGroup>
    );
  }

  // Items with children (but not type="group") - render as collapsible
  if (item.children && item.children.length > 0) {
    return (
      <CollapsibleItem
        key={String(item.key)}
        item={item}
        theme={theme}
        size={size}
        selectedKeys={selectedKeys}
        expandedKeys={expandedKeys}
        onSelect={onSelect}
        onToggleExpand={onToggleExpand}
        isCollapsed={isCollapsed}
      />
    );
  }

  const isSelected = selectedKeys.has(item.key);

  const handleClick = (e: MouseEvent) => {
    if (item.disabled) return;
    item.onClick?.(e);
    onSelect?.(item.key);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (item.disabled) return;
    if (e.key === "Enter") {
      e.preventDefault();
      onSelect?.(item.key);
    }
  };

  // When collapsed, show only icon with tooltip
  if (isCollapsed) {
    const itemKey = String(item.key);
    const collapsedClassName = itemVariants({
      theme,
      size,
      selected: isSelected,
      disabled: !!item.disabled,
      className: cn("justify-center px-0 py-3", item.className),
    });
    const collapsedContent = item.icon ? (
      <span className={iconWrapperVariants({ theme, selected: isSelected, size: "large" })}>
        {item.icon}
      </span>
    ) : null;
    const collapsedEl =
      item.href && !item.disabled ? (
        <a
          href={item.href}
          role="menuitem"
          data-selected={isSelected}
          data-testid={item["data-testid"]}
          className={collapsedClassName}
          onClick={handleClick}
        >
          {collapsedContent}
        </a>
      ) : (
        <div
          role="menuitem"
          tabIndex={item.disabled ? -1 : 0}
          aria-disabled={item.disabled}
          data-selected={isSelected}
          data-testid={item["data-testid"]}
          className={collapsedClassName}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {collapsedContent}
        </div>
      );

    if (item.label) {
      return (
        <Tooltip key={itemKey} content={item.label} placement="right" color="default">
          {collapsedEl}
        </Tooltip>
      );
    }

    return <div key={itemKey}>{collapsedEl}</div>;
  }

  const itemClassName = itemVariants({
    theme,
    size,
    selected: isSelected,
    disabled: !!item.disabled,
    className: item.className,
  });
  const itemContent = (
    <>
      {item.icon && (
        <span className={iconWrapperVariants({ theme, selected: isSelected })}>{item.icon}</span>
      )}
      {typeof item.label === "string" ? (
        item.label
      ) : (
        <span className="flex-1 truncate">{item.label}</span>
      )}
    </>
  );

  return item.href && !item.disabled ? (
    <a
      key={String(item.key)}
      href={item.href}
      role="menuitem"
      data-selected={isSelected}
      data-testid={item["data-testid"]}
      className={itemClassName}
      onClick={handleClick}
    >
      {itemContent}
    </a>
  ) : (
    <div
      key={String(item.key)}
      role="menuitem"
      tabIndex={item.disabled ? -1 : 0}
      aria-disabled={item.disabled}
      data-selected={isSelected}
      data-testid={item["data-testid"]}
      className={itemClassName}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {itemContent}
    </div>
  );
};

/**
 * SidebarNav - Vertical sidebar navigation component
 *
 * A replacement for antd Menu with mode="inline". Supports both declarative
 * children API and items prop API.
 *
 * @example
 * // Using children API
 * <SidebarNav selectedKey="/home" onSelect={({ key }) => navigate(key)}>
 *   <SidebarNav.ItemGroup title="Navigation">
 *     <SidebarNav.Item itemKey="/home" icon={<HomeIcon />}>Home</SidebarNav.Item>
 *     <SidebarNav.Item itemKey="/settings" icon={<SettingsIcon />}>Settings</SidebarNav.Item>
 *   </SidebarNav.ItemGroup>
 * </SidebarNav>
 *
 * @example
 * // Using items API
 * <SidebarNav
 *   theme="dark"
 *   selectedKey={selectedKey}
 *   items={[
 *     { key: "/home", label: "Home", icon: <HomeIcon /> },
 *     { key: "/settings", label: "Settings", icon: <SettingsIcon /> },
 *   ]}
 * />
 */
export const SidebarNav = forwardRef<HTMLDivElement, SidebarNavProps>(
  (
    {
      children,
      className,
      theme = "light",
      size = "sm",
      selectedKey,
      expandedKeys: expandedKeysProp,
      defaultExpandedKeys = [],
      onSelect: onSelectProp,
      onClick,
      onExpandChange,
      items,
      isCollapsed = false,
      ...props
    },
    ref
  ) => {
    const selectedKeys = useMemo(
      () => (selectedKey !== undefined ? new Set([selectedKey]) : new Set<Key>()),
      [selectedKey]
    );

    // Internal state for uncontrolled mode
    const [internalExpandedKeys, setInternalExpandedKeys] = useState<Set<Key>>(
      () => new Set(defaultExpandedKeys)
    );

    // Use controlled value if provided, otherwise use internal state
    const expandedKeys = useMemo(
      () => (expandedKeysProp !== undefined ? new Set(expandedKeysProp) : internalExpandedKeys),
      [expandedKeysProp, internalExpandedKeys]
    );

    const handleSelect = useCallback(
      (key: Key) => {
        onSelectProp?.({ key });
        onClick?.({ key });
      },
      [onSelectProp, onClick]
    );

    const handleToggleExpand = useCallback(
      (key: Key) => {
        const newExpandedKeys = new Set(expandedKeys);
        if (newExpandedKeys.has(key)) {
          newExpandedKeys.delete(key);
        } else {
          newExpandedKeys.add(key);
        }

        // Update internal state for uncontrolled mode
        if (expandedKeysProp === undefined) {
          setInternalExpandedKeys(newExpandedKeys);
        }

        // Notify parent
        onExpandChange?.(Array.from(newExpandedKeys));
      },
      [expandedKeys, expandedKeysProp, onExpandChange]
    );

    const contextValue = useMemo<SidebarNavContextValue>(
      () => ({
        selectedKeys,
        expandedKeys,
        onSelect: handleSelect,
        onToggleExpand: handleToggleExpand,
        theme,
        size,
        isCollapsed,
      }),
      [selectedKeys, expandedKeys, handleSelect, handleToggleExpand, theme, size, isCollapsed]
    );

    return (
      <SidebarNavProvider value={contextValue}>
        <div ref={ref} role="menu" className={sidebarNavVariants({ theme, className })} {...props}>
          {items
            ? items.map(item =>
                renderItem(
                  item,
                  theme,
                  size,
                  selectedKeys,
                  expandedKeys,
                  handleSelect,
                  handleToggleExpand,
                  isCollapsed
                )
              )
            : children}
        </div>
      </SidebarNavProvider>
    );
  }
) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  {
    Item: typeof SidebarNavItem;
    ItemGroup: typeof SidebarNavItemGroup;
    Divider: typeof SidebarNavDivider;
  },
  SidebarNavProps
>;

SidebarNav.displayName = "SidebarNav";
SidebarNav.Item = SidebarNavItem;
SidebarNav.ItemGroup = SidebarNavItemGroup;
SidebarNav.Divider = SidebarNavDivider;
